import { UserData } from "./auth.service";
import { USER_ENDPOINTS } from "../constants/api";
import { getErrorMessage, privateApiClient } from "../utils/axios.utils";

const UserService = {
  // Get all users
  getUsers: async (
    page: number = 1,
    limit: number = 10,
    search: string = "",
    role: string = ""
  ): Promise<{
    users: UserData[];
    totalPages: number;
    totalParams: {
      total: number;
      from: number;
      to: number;
      currentPage: number;
    };
  }> => {
    try {
      const { data } = await privateApiClient.get(USER_ENDPOINTS.GET_ALL, {
        params: { page, limit, search, role },
      });

      const users = data.data.data;
      const pagination = data.data.pagination;

      return {
        users,
        totalPages: pagination.last_page,
        totalParams: {
          total: pagination.total,
          from: pagination.from,
          to: pagination.to,
          currentPage: pagination.current_page,
        },
      };
    } catch (error) {
      throw getErrorMessage(error);
    }
  },

  // Get users by role
  getUsersByRole: async (role: string): Promise<UserData[]> => {
    try {
      const response = await privateApiClient.get(USER_ENDPOINTS.GET_BY_ROLE, {
        params: { role },
      });
      return response.data.data;
    } catch (error) {
      throw getErrorMessage(error);
    }
  },

  // Get user by ID
  getUserById: async (id: number): Promise<UserData> => {
    try {
      const response = await privateApiClient.get(USER_ENDPOINTS.GET_BY_ID(id));
      return response.data.data;
    } catch (error) {
      throw getErrorMessage(error);
    }
  },

  // Create user
  createUser: async (userData: UserData): Promise<UserData> => {
    const response = await privateApiClient.post(
      USER_ENDPOINTS.CREATE,
      userData
    );
    return response.data.data;
  },

  // Update user
  updateUser: async (id: number, userData: UserData): Promise<UserData> => {
    const response = await privateApiClient.put(`/users/${id}`, userData);

    return response.data.data;
  },

  // Delete user
  deleteUser: async (id: number): Promise<void> => {
    await privateApiClient.delete(`/users/${id}`);
  },

  // Update user password
  updatePassword: async (
    id: number,
    passwordData: {
      current_password: string;
      password: string;
      password_confirmation: string;
    }
  ): Promise<UserData> => {
    const response = await privateApiClient.put(
      `/users/${id}/password`,
      passwordData
    );
    return response.data.data;
  },

  // Admin reset user password
  resetUserPassword: async (
    id: number,
    passwordData: {
      password: string;
      password_confirmation: string;
    }
  ): Promise<UserData> => {
    const response = await privateApiClient.put(
      `/users/${id}/reset-password`,
      passwordData
    );
    return response.data.data;
  },
  // Get assignable roles
  getAssignableRoles: async (): Promise<{ id: number; name: string }[]> => {
    try {
      const response = await privateApiClient.get(
        USER_ENDPOINTS.GET_ASSIGNABLE_ROLES
      );
      return response.data.data;
    } catch (error) {
      throw getErrorMessage(error);
    }
  },
};

export default UserService;
